import { ElementType, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import ErrorPage from '@rsos/components/capstone/ErrorPage';
import AppLoading from '@rsos/components/capstone/Layout/AppLoading';

type SectionGateProps = {
  /** True while the section's initialize() is in flight. */
  initializing: boolean;
  /** True once the section has finished initializing successfully. */
  initialized: boolean;
  /** True when initialization failed. */
  hasError: boolean;
  /**
   * Optional layout wrapper (e.g. AppInnerWrapper / AppIaRWrapper) applied
   * around the ready content.
   */
  wrapper?: ElementType;
  /**
   * Ready content. Defaults to the router <Outlet /> so a section's sub-routes
   * (defined in app/Main.tsx) render through the gate.
   */
  children?: ReactNode;
};

// Shared initialization gate for every admin section. Authorization is not its
// job — that belongs to the per-route <RequireAccess> guards in app/Main.tsx.
// Each section wires up its own selector + initialize() effect and hands the
// resulting status here; this component owns the (previously copy-pasted)
// loading/error/ready ladder so the behavior stays identical across sections.
const SectionGate = ({
  initializing,
  initialized,
  hasError,
  wrapper: Wrapper,
  children = <Outlet />,
}: SectionGateProps) => {
  if (hasError) {
    return <ErrorPage />;
  }
  if (initializing) {
    return <AppLoading />;
  }
  if (initialized) {
    return Wrapper ? <Wrapper>{children}</Wrapper> : <>{children}</>;
  }
  return <ErrorPage />;
};

export default SectionGate;
